/**
 * Provider selector component for multi-provider authentication.
 *
 * Displays available auth providers and allows users to choose one before login.
 */

import React, { useEffect, useState } from 'react';
import {
  AuthProviderType,
  EnabledProvidersResponse,
  setSelectedProvider,
  getDefaultProvider,
} from './authConfig';

interface ProviderSelectorProps {
  onProviderSelected: (provider: AuthProviderType) => void;
  enabledProviders?: EnabledProvidersResponse | null;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Provider card component for displaying a single auth provider option.
 */
function ProviderCard({
  type,
  name,
  description,
  icon,
  onClick,
  isDefault,
}: {
  type: AuthProviderType;
  name: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  isDefault?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full p-6 rounded-lg border-2 border-gray-200 dark:border-gray-700
                 hover:border-primary-500 dark:hover:border-primary-400
                 bg-white dark:bg-gray-800
                 transition-all duration-200
                 group focus:outline-none focus:ring-2 focus:ring-primary-500"
      data-testid={`provider-${type}`}
    >
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-lg
                       bg-gray-100 dark:bg-gray-700
                       group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30
                       transition-colors">
          {icon}
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
            {isDefault && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full
                             bg-primary-100 dark:bg-primary-900/30
                             text-primary-700 dark:text-primary-300">
                Recommended
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {description}
          </p>
        </div>
        <div className="flex-shrink-0 text-gray-400 group-hover:text-primary-500 transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
}

/**
 * Auth0 icon component.
 */
function Auth0Icon() {
  return (
    <svg className="w-8 h-8 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21.98 7.448L19.62 0H4.347L2.02 7.448c-1.352 4.312.03 9.206 3.815 12.015L12.007 24l6.157-4.552c3.755-2.81 5.182-7.688 3.815-12.015l-6.16 4.58 2.343 7.45-6.157-4.597-6.158 4.58 2.358-7.433-6.188-4.55 7.63-.045L12.008 0l2.356 7.404 7.615.044z" />
    </svg>
  );
}

/**
 * Authentik icon component.
 */
function AuthentikIcon() {
  return (
    <svg className="w-8 h-8 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </svg>
  );
}

/**
 * Provider selector component.
 *
 * Shows available auth providers and handles selection.
 */
export function ProviderSelector({
  onProviderSelected,
  enabledProviders,
  isLoading,
  error,
}: ProviderSelectorProps) {
  const [selectedDefault, setSelectedDefault] = useState<AuthProviderType | null>(null);

  useEffect(() => {
    // Determine the default provider
    if (enabledProviders?.default_provider) {
      setSelectedDefault(enabledProviders.default_provider as AuthProviderType);
    } else {
      setSelectedDefault(getDefaultProvider());
    }
  }, [enabledProviders]);

  const handleProviderSelect = (provider: AuthProviderType) => {
    setSelectedProvider(provider);
    onProviderSelected(provider);
  };

  // Check if providers are available
  const auth0Provider = enabledProviders?.providers.find(p => p.type === 'auth0');
  const authentikProvider = enabledProviders?.providers.find(p => p.type === 'authentik');

  // If only one provider is enabled, auto-select it
  useEffect(() => {
    if (enabledProviders && enabledProviders.providers.length === 1) {
      const provider = enabledProviders.providers[0].type as AuthProviderType;
      handleProviderSelect(provider);
    }
  }, [enabledProviders]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Loading authentication providers...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-medium text-red-800 dark:text-red-200">
              Failed to load authentication providers
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // If no providers from backend, show default Auth0
  const hasAuth0 = auth0Provider || !enabledProviders;
  const hasAuthentik = authentikProvider;

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Choose your sign-in method
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Select your organization's authentication provider
        </p>
      </div>

      <div className="space-y-3">
        {hasAuth0 && (
          <ProviderCard
            type="auth0"
            name="Auth0"
            description="Sign in with your Auth0 account"
            icon={<Auth0Icon />}
            onClick={() => handleProviderSelect('auth0')}
            isDefault={selectedDefault === 'auth0'}
          />
        )}

        {hasAuthentik && (
          <ProviderCard
            type="authentik"
            name="Authentik"
            description="Sign in with your Authentik account"
            icon={<AuthentikIcon />}
            onClick={() => handleProviderSelect('authentik')}
            isDefault={selectedDefault === 'authentik'}
          />
        )}
      </div>

      {!hasAuth0 && !hasAuthentik && (
        <div className="text-center p-6 text-gray-600 dark:text-gray-400">
          No authentication providers are currently configured.
          Please contact your administrator.
        </div>
      )}
    </div>
  );
}

export default ProviderSelector;
